import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowDownRight, ArrowUpRight, Ban, ChevronDown, Sparkles } from 'lucide-react';
import type { MarketTradeIdea, MarketTradeIdeasPayload } from '@/services/api';
import { formatPct, pctChipClass } from './AnalogLensAccordion';

type Props = {
  tradeIdeas: MarketTradeIdeasPayload;
  embedded?: boolean;
  className?: string;
  hideHeader?: boolean;
  maxVisible?: number;
};

function convictionClass(conviction: string | undefined): string {
  switch (conviction?.toLowerCase()) {
    case 'high':
      return 'bg-gray-900 text-white';
    case 'medium':
      return 'bg-gray-200 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function directionClass(direction: string | undefined): string {
  switch (direction?.toLowerCase()) {
    case 'short':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'avoid':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    default:
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
}

function directionLabel(direction: string | undefined): string {
  const dir = direction?.toLowerCase();
  if (dir === 'avoid') return 'avoid';
  if (dir === 'short') return 'short';
  return direction ?? 'long';
}

function tradeKey(trade: MarketTradeIdea, index: number): string {
  return `${trade.symbol}-${index}`;
}

function ForwardReturnChips({ trade }: { trade: MarketTradeIdea }) {
  const fwd = trade.forward_returns;
  if (!fwd) return null;

  const chips: { label: string; median: number; hitRate?: number }[] = [];
  if (fwd.t5?.median_pct != null) {
    chips.push({ label: '5d', median: fwd.t5.median_pct, hitRate: fwd.t5.hit_rate });
  }
  if (fwd.t10?.median_pct != null) {
    chips.push({ label: '10d', median: fwd.t10.median_pct, hitRate: fwd.t10.hit_rate });
  }
  if (fwd.t20?.median_pct != null) {
    chips.push({ label: '20d', median: fwd.t20.median_pct, hitRate: fwd.t20.hit_rate });
  }
  if (!chips.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <span
          key={chip.label}
          className={`rounded-lg bg-white px-2 py-0.5 text-[11px] font-medium tabular-nums ring-1 ring-gray-200 ${pctChipClass(chip.median)}`}
          title={
            chip.hitRate != null
              ? `${chip.label} median · ${chip.hitRate.toFixed(0)}% analogs positive`
              : `${chip.label} analog median`
          }
        >
          {chip.label} {formatPct(chip.median)}
        </span>
      ))}
    </div>
  );
}

function TradeIdeaRow({
  trade,
  isOpen,
  onToggle,
}: {
  trade: MarketTradeIdea;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const dir = trade.direction?.toLowerCase();
  const isShort = dir === 'short';
  const isAvoid = dir === 'avoid';
  const DirectionIcon = isShort ? ArrowDownRight : isAvoid ? Ban : ArrowUpRight;
  const headlinePct = trade.headline_pct ?? trade.forward_returns?.t5?.median_pct ?? null;
  const headlineLabel = trade.headline_label ?? '5d';
  const headlineDetail =
    trade.headline_detail ??
    (trade.forward_returns?.t5?.hit_rate != null && trade.forward_returns.t5.total != null
      ? `${trade.forward_returns.t5.hit_rate.toFixed(0)}% of ${trade.forward_returns.t5.total} analogs positive`
      : '');

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors md:px-5 ${
          isOpen ? 'bg-gray-50' : 'hover:bg-gray-50/60'
        }`}
      >
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold ${
            isOpen ? 'bg-violet-600 text-white' : 'bg-violet-50 text-violet-700'
          }`}
        >
          {trade.symbol.slice(0, 4)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{trade.symbol}</span>
            <span
              className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-medium capitalize ${directionClass(trade.direction)}`}
            >
              <DirectionIcon className="h-3 w-3" />
              {directionLabel(trade.direction)}
            </span>
            {trade.asset_type ? (
              <span className="rounded-lg bg-gray-100 px-2 py-0.5 text-[11px] font-medium capitalize text-gray-600">
                {trade.asset_type}
              </span>
            ) : null}
            {headlinePct != null ? (
              <span
                className={`rounded-lg bg-white px-2 py-0.5 text-[11px] font-medium tabular-nums ring-1 ring-gray-200 ${pctChipClass(headlinePct)}`}
              >
                {headlineLabel} {formatPct(headlinePct)}
              </span>
            ) : null}
            {trade.conviction ? (
              <span
                className={`rounded-lg px-2 py-0.5 text-[11px] font-medium capitalize ${convictionClass(trade.conviction)}`}
              >
                {trade.conviction}
              </span>
            ) : null}
          </div>
          {!isOpen ? (
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {trade.name}
              {headlineDetail ? ` · ${headlineDetail}` : ''}
            </p>
          ) : null}
        </div>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100 px-4 py-4 md:px-5">
              {trade.name ? (
                <p className="mb-2 text-xs text-gray-500">{trade.name}</p>
              ) : null}

              <div className="mb-3">
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">
                  Analog forward returns
                </p>
                <ForwardReturnChips trade={trade} />
                {trade.analog_count != null ? (
                  <p className="mt-1.5 text-xs text-gray-400">
                    Based on {trade.analog_count} situational analogs
                  </p>
                ) : null}
              </div>

              {trade.thesis ? (
                <p className="mb-3 text-sm leading-relaxed text-gray-700">{trade.thesis}</p>
              ) : null}
              {trade.entry_trigger ? (
                <div className="mb-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    Entry trigger
                  </p>
                  <p className="text-sm text-gray-600">{trade.entry_trigger}</p>
                </div>
              ) : null}
              {trade.risk ? (
                <div className="mb-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Risk</p>
                  <p className="text-sm text-gray-600">{trade.risk}</p>
                </div>
              ) : null}
              {trade.tags?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {trade.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-500"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export function MarketTradeIdeasPanel({
  tradeIdeas,
  embedded = false,
  className = '',
  hideHeader = false,
  maxVisible,
}: Props) {
  const allTrades = tradeIdeas.trades ?? [];
  const directionOrder: Record<string, number> = { long: 0, short: 1, avoid: 2 };
  const sortedTrades = [...allTrades].sort(
    (a, b) =>
      (directionOrder[a.direction?.toLowerCase() ?? 'long'] ?? 9) -
      (directionOrder[b.direction?.toLowerCase() ?? 'long'] ?? 9),
  );
  const trades = maxVisible != null ? sortedTrades.slice(0, maxVisible) : sortedTrades;
  const [openKey, setOpenKey] = useState<string | null>(null);

  if (allTrades.length === 0) return null;

  const analogCount =
    tradeIdeas.metadata?.analog_count ??
    trades.find((t) => t.analog_count != null)?.analog_count ??
    null;

  const handleToggle = (key: string) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.15 }}
      className={
        embedded
          ? `border-b border-gray-100 bg-gray-50/30 ${className}`
          : `overflow-hidden rounded-3xl border border-gray-200 bg-white ${className}`
      }
      aria-label="AI-generated trade ideas"
    >
      {hideHeader ? null : (
        <div
          className={
            embedded
              ? 'border-t border-gray-100 px-6 py-5 md:px-8'
              : 'border-b border-gray-100 px-6 py-5 md:px-8 md:py-6'
          }
        >
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-600">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
              Analog Trade Ideas
            </span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
              {allTrades.length} ideas
            </span>
            {analogCount != null ? (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                {analogCount} analogs
              </span>
            ) : null}
          </div>
          <h3 className={`font-semibold tracking-tight text-gray-900 ${embedded ? 'text-base' : 'text-xl'}`}>
            Top longs & shorts after similar setups
          </h3>
          {tradeIdeas.situation_headline ? (
            <p className="mt-2 text-sm leading-relaxed text-gray-500">
              {tradeIdeas.situation_headline}
            </p>
          ) : null}
        </div>
      )}

      <div className={`space-y-2 ${embedded ? 'px-6 pb-5 md:px-8' : 'p-4 md:p-6'}`}>
        {trades.map((trade, index) => {
          const key = tradeKey(trade, index);
          return (
            <TradeIdeaRow
              key={key}
              trade={trade}
              isOpen={openKey === key}
              onToggle={() => handleToggle(key)}
            />
          );
        })}
      </div>

      {tradeIdeas.disclaimer ? (
        <p
          className={`text-xs leading-relaxed text-gray-400 ${
            embedded ? 'px-6 pb-5 md:px-8' : 'border-t border-gray-100 px-6 py-4 md:px-8'
          }`}
        >
          {tradeIdeas.disclaimer}
        </p>
      ) : null}
    </motion.section>
  );
}
